import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Phone, Stethoscope, CheckCircle2, CalendarPlus, CreditCard, Banknote, Landmark, Info } from "lucide-react";
import { downloadIcs } from "@/lib/ics";
import Seo from "@/components/Seo";
import { Helmet } from "react-helmet-async";
import { drName, formatDateLong } from "@/lib/utils";

function nextDays(n) {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    arr.push(d);
  }
  return arr;
}

function fmt(d) { return d.toISOString().slice(0, 10); }

export default function DoctorProfile() {
  const { id: routeId, profileSlug } = useParams();
  const { t, lang } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const isSlug = !!profileSlug && profileSlug.startsWith("dr-");
  const [doc, setDoc] = useState(null);
  const [selectedDate, setSelectedDate] = useState(fmt(new Date()));
  const [slotsData, setSlotsData] = useState([]);
  const [slotDuration, setSlotDuration] = useState(30);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "", notes: "",
    booking_for: "self", family_first_name: "", family_last_name: "", family_dob: "", family_relationship: "hijo/a" });
  const [confirmedApt, setConfirmedApt] = useState(null);
  const [demoBlocked, setDemoBlocked] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Guard: if we hit the :profileSlug catch-all with a slug that is NOT a
    // doctor profile (i.e. doesn't start with dr-), bounce to home. This
    // prevents /random-string from rendering an empty DoctorProfile.
    if (profileSlug && !profileSlug.startsWith("dr-")) {
      nav("/", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (isSlug) {
          // /dr-... route → look up by slug
          const slug = profileSlug;
          try {
            const r = await api.get(`/doctors/by-slug/${slug}`);
            if (cancelled) return;
            // Backend may return {redirect_to} when hit via slug_history —
            // navigate to the canonical URL so /old 301-equivalent to /new.
            if (r.data?.redirect_to) {
              nav("/" + r.data.redirect_to, { replace: true });
            } else {
              setDoc(r.data);
            }
          } catch { /* 404 handled by <Seo noindex> */ }
        } else {
          // Legacy /doctor/:id — resolve and 301-style client-side redirect
          // to the SEO-friendly slug URL when available.
          const r = await api.get(`/doctors/${routeId}`);
          if (cancelled) return;
          if (r.data.profile_slug) {
            nav("/" + r.data.profile_slug, { replace: true });
          } else {
            setDoc(r.data);
          }
        }
      } catch { /* 404 handled by <Seo noindex> */ }
    })();
    return () => { cancelled = true; };
  }, [routeId, profileSlug, loc.pathname, isSlug, nav]);

  const idForApi = doc?.id || routeId;
  useEffect(() => {
    if (doc && idForApi) api.get(`/doctors/${idForApi}/slots`, { params: { date: selectedDate } })
              .then(r => { setSlotsData(r.data.slots || []); setSlotDuration(r.data.duration_min || 30); });
  }, [idForApi, selectedDate, doc]);

  const openBooking = (time) => { setSelectedSlot(time); setOpen(true); };

  const submit = async () => {
    if (!form.patient_name || !form.patient_email || !form.patient_phone) {
      toast.error("Completa nombre, email y teléfono"); return;
    }
    setLoading(true);
    try {
      const res = await api.post("/appointments", {
        doctor_id: idForApi, date: selectedDate, time: selectedSlot, ...form,
      });
      setConfirmedApt({ id: res.data.id, date: selectedDate, time: selectedSlot });
      api.get(`/doctors/${idForApi}/slots`, { params: { date: selectedDate } }).then(r => setSlotsData(r.data.slots || []));
      toast.success(t.booking.success);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Test / demo profile — show the dedicated demo screen inside the dialog
      // rather than a toast. No appointment was created server-side.
      if (detail && typeof detail === "object" && detail.code === "demo_profile") {
        setDemoBlocked(detail[lang] || detail.es || "");
      } else {
        toast.error(typeof detail === "string" ? detail : "Error");
      }
    } finally { setLoading(false); }
  };

  if (!doc) return <div className="max-w-7xl mx-auto p-8 text-slate-500">{t.common.loading}</div>;

  const days = nextDays(14);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
      {(() => {
        const t = doc.title || "Dr.";
        const seoTitle = `${t} ${doc.full_name} - ${doc.specialty} en ${doc.city}`;
        const article = t === "Dra." ? "la Dra." : "el Dr.";
        const seoDesc = `Reserve su cita médica con ${article} ${doc.full_name}, especialista en ${doc.specialty} en ${doc.city}. Consulta horarios disponibles y agende en línea.`;
        // Physician JSON-LD for Google rich results. Only for real (non-demo)
        // approved doctors — demo profiles carry noindex and must not surface
        // structured data.
        const jsonLd = (!doc.is_demo && doc.active !== false) ? {
          "@context": "https://schema.org",
          "@type": "Physician",
          "name": `${t} ${doc.full_name}`,
          "medicalSpecialty": doc.specialty,
          "url": `https://www.agendik.io/${doc.profile_slug || `doctor/${doc.id}`}`,
          ...(doc.photo_url ? { "image": doc.photo_url } : {}),
          ...(doc.consultation_price_usd ? { "priceRange": `$${doc.consultation_price_usd}` } : {}),
          "address": {
            "@type": "PostalAddress",
            "streetAddress": doc.address,
            "addressLocality": doc.city,
            "addressCountry": "EC",
          },
          "telephone": doc.phone,
        } : null;
        return (
          <>
            <Seo title={seoTitle} description={seoDesc}
                 image={doc.photo_url || undefined}
                 noindex={!!doc.is_demo || doc.active === false}/>
            {jsonLd && (
              <Helmet><script type="application/ld+json">{JSON.stringify(jsonLd)}</script></Helmet>
            )}
          </>
        );
      })()}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-6">
            {doc.photo_url ? (
              <img src={doc.photo_url} alt={doc.full_name}
                   className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl object-cover border border-slate-200"/>
            ) : (
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl bg-blue-50 flex items-center justify-center">
                <Stethoscope size={40} className="text-blue-600"/>
              </div>
            )}
            <div className="flex-1">
              <h1 data-testid="doctor-name" className="text-2xl sm:text-3xl font-bold text-slate-900">{drName(doc)}</h1>
              <div className="text-blue-600 font-semibold mt-1">{doc.specialty}</div>
              {doc.presentation && (
                <p data-testid="doctor-presentation" className="mt-3 text-sm text-slate-700 leading-relaxed">
                  {doc.presentation}
                </p>
              )}
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                <div className="flex items-center gap-2"><MapPin size={14}/> {doc.clinic_name} · {doc.address}, {doc.city}</div>
                <div className="flex items-center gap-2"><Phone size={14}/> {doc.phone}</div>
              </div>
              <div className="mt-3 inline-flex items-center gap-2 text-xs bg-slate-100 text-slate-500 rounded-full px-3 py-1.5 border border-slate-200">
                <span className="w-2 h-2 rounded-full bg-slate-300"/> Teleconsulta · <span className="font-semibold">Próximamente</span>
              </div>
            </div>
          </div>

          {/* Public extras: price, payments, insurances */}
          {(doc.consultation_price_usd != null || (doc.payment_methods||[]).length > 0 || (doc.insurances||[]).length > 0 || doc.accepts_iess) && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {doc.consultation_price_usd != null && (
                <div data-testid="public-price" className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.docFields.publicPriceLabel}</div>
                  <div className="text-2xl font-bold text-slate-900 mt-1">${doc.consultation_price_usd}</div>
                </div>
              )}
              {(doc.payment_methods||[]).length > 0 && (
                <div data-testid="public-payments" className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.docFields.publicPaymentTitle}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                    {doc.payment_methods.includes("card_credit") && <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 border border-blue-100"><CreditCard size={12}/> {t.docFields.pmCredit}</span>}
                    {doc.payment_methods.includes("card_debit") && <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 border border-blue-100"><CreditCard size={12}/> {t.docFields.pmDebit}</span>}
                    {doc.payment_methods.includes("cash") && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 border border-emerald-100"><Banknote size={12}/> {t.docFields.pmCash}</span>}
                    {doc.payment_methods.includes("transfer") && <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 text-slate-700 px-2.5 py-1 border border-slate-100"><Landmark size={12}/> {t.docFields.pmTransfer}</span>}
                    {doc.payment_methods.includes("deuna") && <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 px-2.5 py-1 border border-purple-100">DeUna</span>}
                    {doc.payment_methods.includes("other") && <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 text-slate-700 px-2.5 py-1 border border-slate-100">Otro</span>}
                  </div>
                </div>
              )}
              {((doc.insurances||[]).length > 0 || doc.accepts_iess) && (
                <div data-testid="public-insurances" className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.docFields.publicInsurancesTitle}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                    {doc.accepts_iess && <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 px-2.5 py-1 border border-amber-100 font-semibold">IESS</span>}
                    {(doc.insurances||[]).map((s, i) => (
                      <span key={i} className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 border border-slate-200">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">{t.profile.pickDate}</h2>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {days.map(d => {
                const iso = fmt(d);
                const active = iso === selectedDate;
                return (
                  <button key={iso} data-testid={`date-${iso}`} onClick={() => setSelectedDate(iso)}
                          className={`flex-shrink-0 px-3 py-2 rounded-xl border text-center min-w-[68px] transition-all ${active ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}>
                    <div className="text-xs font-medium">{t.days[(d.getDay()+6)%7]}</div>
                    <div className="text-lg font-bold">{d.getDate()}</div>
                  </button>
                );
              })}
            </div>

            <h3 className="text-md font-semibold text-slate-900 mt-6 mb-3">{t.profile.availableSlots} <span className="text-xs text-slate-400 font-normal">· hora de Ecuador (UTC-5)</span></h3>
            {slotsData.length === 0 ? (
              <p className="text-slate-500 text-sm">{t.profile.noSlots}</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
                {slotsData.map(s => (
                  <button key={s.time} data-testid={`slot-${s.time}`} disabled={!s.available}
                          onClick={() => openBooking(s.time)}
                          className="slot-btn flex flex-col items-center py-2">
                    <span className="font-semibold">{s.time}</span>
                    <span className="text-[10px] font-normal opacity-70 mt-0.5">{slotDuration} min</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setConfirmedApt(null); setDemoBlocked(null); } }}>
        <DialogContent data-testid="booking-modal" className="max-w-md">
          {demoBlocked ? (
            <div data-testid="booking-demo-screen" className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <Info className="text-amber-600" size={28}/>
              </div>
              <DialogTitle className="text-xl">Agendik</DialogTitle>
              <p data-testid="demo-blocked-msg" className="text-slate-700 mt-3 text-sm leading-relaxed">
                {demoBlocked}
              </p>
              <Button data-testid="demo-blocked-cta"
                      onClick={() => { setOpen(false); setDemoBlocked(null); nav("/buscar"); }}
                      className="w-full mt-5 bg-blue-600 hover:bg-blue-700 min-h-[44px]">
                {lang === "fr" ? "Chercher des médecins" : lang === "en" ? "Find doctors" : "Buscar doctores"}
              </Button>
            </div>
          ) : confirmedApt ? (
            <div className="text-center py-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="text-emerald-600" size={30}/>
              </div>
              <DialogTitle className="text-xl">{t.booking.success}</DialogTitle>
              <p className="text-slate-600 mt-2 text-sm">{t.booking.successMsg}</p>
              <div className="bg-blue-50 rounded-xl p-4 mt-4 text-sm text-slate-700">
                <div><strong>{formatDateLong(confirmedApt.date, lang)}</strong></div>
                <div className="mt-0.5">{confirmedApt.time} · Ecuador (UTC-5)</div>
                <div className="text-xs text-slate-500 mt-2">ID: {confirmedApt.id}</div>
              </div>
              <p data-testid="post-booking-hint" className="text-[11px] text-slate-500 mt-3">
                {t.booking.manageHint}
              </p>
              <Button data-testid="add-calendar-btn"
                      onClick={() => downloadIcs({
                        title: `Cita ${drName(doc)}`,
                        description: `${doc.specialty} · ${doc.clinic_name}`,
                        location: `${doc.address}, ${doc.city}`,
                        date: confirmedApt.date, time: confirmedApt.time,
                        durationMin: slotDuration,
                      })}
                      variant="outline"
                      className="w-full mt-3 border-blue-200 text-blue-700 hover:bg-blue-50">
                <CalendarPlus size={16} className="mr-2"/> {t.home.addToCalendar}
              </Button>
              <Button data-testid="booking-close-btn" onClick={() => { setOpen(false); setConfirmedApt(null); }}
                      className="w-full mt-2 bg-blue-600 hover:bg-blue-700">{t.booking.close}</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t.booking.title}</DialogTitle>
                <p className="text-sm text-slate-500">{drName(doc)} · {selectedDate} · {selectedSlot}</p>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg text-xs">
                  <button data-testid="booking-for-self" onClick={() => setForm({...form, booking_for: "self"})}
                          className={`flex-1 py-2 rounded-md font-semibold ${form.booking_for === "self" ? "bg-white shadow" : "text-slate-500"}`}>
                    Para mí
                  </button>
                  <button data-testid="booking-for-family" onClick={() => setForm({...form, booking_for: "family"})}
                          className={`flex-1 py-2 rounded-md font-semibold ${form.booking_for === "family" ? "bg-white shadow" : "text-slate-500"}`}>
                    Para un familiar
                  </button>
                </div>
                <div>
                  <Label>{form.booking_for === "family" ? t.booking.holderName : t.booking.fullName}</Label>
                  <Input data-testid="booking-name-input" value={form.patient_name}
                         onChange={e => setForm({...form, patient_name: e.target.value})}/>
                </div>
                {form.booking_for === "family" && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid="family-first" placeholder="Nombre familiar" value={form.family_first_name}
                             onChange={e => setForm({...form, family_first_name: e.target.value})}/>
                      <Input data-testid="family-last" placeholder="Apellido familiar" value={form.family_last_name}
                             onChange={e => setForm({...form, family_last_name: e.target.value})}/>
                    </div>
                    <div>
                      <Label className="text-xs">{t.home.dobLabel}</Label>
                      <Input data-testid="family-dob" placeholder="DD/MM/AAAA" maxLength={10}
                             value={form.family_dob}
                             onChange={e => {
                               let v = e.target.value.replace(/[^\d/]/g, "");
                               if (v.length === 2 && form.family_dob.length === 1) v += "/";
                               else if (v.length === 5 && form.family_dob.length === 4) v += "/";
                               setForm({...form, family_dob: v});
                             }}/>
                    </div>
                    <select data-testid="family-rel" value={form.family_relationship}
                            onChange={e => setForm({...form, family_relationship: e.target.value})}
                            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                      <option value="hijo/a">Hijo/a</option>
                      <option value="esposo/a">Esposo/a</option>
                      <option value="padre/madre">Padre/Madre</option>
                      <option value="otro">Otro</option>
                    </select>
                  </>
                )}
                <div>
                  <Label>{form.booking_for === "family" ? t.booking.holderEmail : t.booking.email}</Label>
                  <Input data-testid="booking-email-input" type="email" value={form.patient_email}
                         onChange={e => setForm({...form, patient_email: e.target.value})}/>
                </div>
                <div>
                  <Label>{form.booking_for === "family" ? t.booking.holderPhone : t.booking.phone}</Label>
                  <Input data-testid="booking-phone-input" value={form.patient_phone}
                         onChange={e => setForm({...form, patient_phone: e.target.value})}/>
                </div>
                <div>
                  <Label>{t.booking.notes}</Label>
                  <Textarea data-testid="booking-notes-input" value={form.notes}
                            onChange={e => setForm({...form, notes: e.target.value})} rows={2}/>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button data-testid="booking-cancel-btn" variant="outline" onClick={() => setOpen(false)}>{t.booking.back}</Button>
                <Button data-testid="booking-confirm-btn" disabled={loading} onClick={submit}
                        className="bg-blue-600 hover:bg-blue-700">{t.booking.confirm}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
